import { type ReactNode, useState, useMemo } from "react";
import { getFilterDisplayName } from "../../utils/getFilterDisplayName";
import { Show, BaseInput, InputSize, Checkbox } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  ArrowUp01Icon
} from "@hugeicons-pro/core-stroke-rounded";

const filterOptionsBySearch = (
  options: { id?: string; name: string; count?: number }[],
  search: string
) => {
  return options.filter((option) =>
    option.name.toLowerCase().includes(search.toLowerCase())
  );
};

export const FilterSection = ({
  title,
  options = [],
  optionLimit,
  selectedOptions = [],
  onChange,
  showSearch = true,
  formatLabel = getFilterDisplayName,
  emptyText = "No options available",
  badge,
  children
}: {
  title: string;
  badge?: string;
  options?: { id?: string; name: string; count?: number }[];
  optionLimit?: number;
  selectedOptions?: string[];
  onChange?: (name: string) => void;
  showSearch?: boolean;
  formatLabel?: (name: string) => string;
  emptyText?: string;
  children?: ReactNode;
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(
    () => filterOptionsBySearch(options, search),
    [options, search]
  );

  const displayedOptions = useMemo(
    () =>
      showAll || !optionLimit
        ? filteredOptions
        : filteredOptions.slice(0, optionLimit),
    [showAll, optionLimit, filteredOptions]
  );

  const hasMoreOptionsThanLimit =
    optionLimit && filteredOptions.length > optionLimit;
  const shouldShowMoreButton = hasMoreOptionsThanLimit;

  return (
    <div className="py-4 px-2">
      <button
        className="w-full flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <p className="flex items-center gap-2 text-content-1000 text-sm font-bold">
          {title}
          <Show if={Boolean(badge)}>
            <span className="rounded-full bg-primary-20 px-1.5 py-0.5 text-xxs font-bold text-primary-500">
              {badge}
            </span>
          </Show>
        </p>
        <HugeiconsIcon icon={expanded ? ArrowUp01Icon : ArrowDown01Icon} />
      </button>

      <Show if={expanded && Boolean(children)}>
        <div className="mt-4">{children}</div>
      </Show>

      <Show if={expanded && !children}>
        <div className="mt-4">
          <Show if={showSearch && !title.toLowerCase().includes("status")}>
            <BaseInput
              id="search"
              autoComplete="off"
              placeholder="Search..."
              value={search}
              onChange={setSearch}
              size={InputSize.SM}
              className="h-12 mb-4"
            />
          </Show>

          <Show if={!displayedOptions.length}>
            <p className="text-sm text-content-400">
              {options.length ? "No options available" : emptyText}
            </p>
          </Show>

          <div
            className="space-y-2"
            style={{
              maskImage:
                shouldShowMoreButton && !showAll
                  ? "linear-gradient(to bottom, rgba(0, 0, 0, 1.0) 0%, transparent 100%)"
                  : "none"
            }}
          >
            {displayedOptions.map((option) => (
              <Checkbox
                key={option.name}
                // Scoped by section: a tag can share its name with a source or stage.
                id={`${title}-${option.name}`}
                name={option.name}
                checked={selectedOptions.includes(option.id ?? option.name)}
                label={
                  option.count !== undefined
                    ? `${formatLabel(option.name)} (${option.count})`
                    : formatLabel(option.name)
                }
                labelClassName="whitespace-normal"
                onChange={() => onChange?.(option.id ?? option.name)}
              />
            ))}
          </div>
          <Show if={shouldShowMoreButton}>
            <button
              className="text-primary-500 text-sm mt-2 font-bold"
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? "Show less" : "Show more"}
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
};
